/**
 * In-memory event queue that batches audit events and flushes them to
 * POST /api/audit-logs/events using native fetch (not the axios instance, to
 * avoid triggering the audit interceptors recursively).
 *
 * Flush triggers:
 *   - Automatically after FLUSH_INTERVAL_MS (3 s) of inactivity
 *   - Immediately when MAX_BATCH (10) events accumulate
 *   - On page unload via fetch({ keepalive: true })
 *
 * Failed flushes are retried with exponential back-off up to MAX_RETRIES times,
 * then dropped silently — audit logging is best-effort and must never affect UX.
 */
import authStore from '../../store/auth';
import { USER_ROLES } from '../../consts/userRoles';
import { sanitize } from './sanitize';
import notificationsStore from '../../store/notifications';

// Reviewer accounts get 403 on POST /api/audit-logs/events, so skip the
// entire enqueue → flush chain for that role. Read via authStore so the
// gate updates when the current-user query hydrates the role after login.
function _isSuppressedRole() {
  try {
    return authStore.role === USER_ROLES.APPROVER;
  } catch {
    return false;
  }
}

const MAX_BATCH = 10;
const FLUSH_INTERVAL_MS = 3000;
const MAX_RETRIES = 3;

let _queue = [];
let _timer = null;
let _retries = 0;

function _ingestUrl() {
  try {
    const raw = import.meta.env?.VITE_BASE_URL ?? '';
    const base = raw && !raw.endsWith('/') ? `${raw}/` : raw;
    return `${base}api/audit-logs/events`;
  } catch {
    return 'api/audit-logs/events';
  }
}

function _headers() {
  const h = { 'Content-Type': 'application/json' };
  try {
    const token = authStore.token;
    if (token) h['Authorization'] = `Bearer ${token}`;
    const company = localStorage.getItem('activeCompanyId');
    if (company) h['X-Active-Company-Id'] = company;
  } catch (_) {
    /* ignore missing auth */
  }
  return h;
}

function _clearTimer() {
  if (_timer) {
    clearTimeout(_timer);
    _timer = null;
  }
}

function _genId() {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export async function flush() {
  if (_isSuppressedRole()) {
    // Drop any already-enqueued events that landed before the role was
    // known — reviewers must not hit the events endpoint at all.
    _queue = [];
    _clearTimer();
    return;
  }
  if (_queue.length === 0) return;
  const batch = _queue.splice(0);
  _clearTimer();
  try {
    const resp = await fetch(_ingestUrl(), {
      method: 'POST',
      headers: _headers(),
      body: JSON.stringify(batch)
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    _retries = 0;
    // Notify the UI for each successfully persisted event
    const now = new Date().toISOString();
    for (const event of batch) {
      try {
        notificationsStore.addNotification({ ...event, id: _genId(), timestamp: now });
      } catch (_) {
        /* ignore notification errors */
      }
    }
  } catch {
    _retries += 1;
    if (_retries <= MAX_RETRIES) {
      // Re-queue the batch and schedule a retry with exponential back-off
      _queue.unshift(...batch);
      const delay = Math.min(1000 * Math.pow(2, _retries - 1), 30000);
      _timer = setTimeout(() => {
        _timer = null;
        flush();
      }, delay);
    }
    // After MAX_RETRIES failures, the batch is dropped silently
  }
}

export function enqueue(event) {
  if (_isSuppressedRole()) return;
  _queue.push(sanitize(event));
  if (_queue.length >= MAX_BATCH) {
    flush();
  } else if (!_timer) {
    _timer = setTimeout(() => {
      _timer = null;
      flush();
    }, FLUSH_INTERVAL_MS);
  }
}

// Flush remaining events when the user navigates away.
// keepalive: true allows the fetch to survive the page unload.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (_isSuppressedRole()) return;
    if (_queue.length === 0) return;
    fetch(_ingestUrl(), {
      method: 'POST',
      headers: _headers(),
      body: JSON.stringify(_queue.splice(0)),
      keepalive: true
    }).catch(() => {});
  });
}
